import React, { forwardRef, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon, CloseIcon, SearchIcon } from './icons';

const cx = (...names) => names.filter(Boolean).join(' ');

/** Label, hint and error around a control; ids wired for aria-describedby. */
function FieldFrame({ id, label, hideLabel, hint, error, className, children }) {
  return (
    <div className={cx('ui-field', error && 'has-error', className)}>
      {label && (
        <label htmlFor={id} className={cx('ui-field__label', hideLabel && 'visually-hidden')}>{label}</label>
      )}
      {children}
      {hint && !error && <p id={`${id}-hint`} className="ui-field__hint">{hint}</p>}
      {error && <p id={`${id}-error`} className="ui-field__error">{error}</p>}
    </div>
  );
}

const describedBy = (id, hint, error) => (error ? `${id}-error` : hint ? `${id}-hint` : undefined);

export const TextField = forwardRef(function TextField(
  { label, hideLabel, hint, error, icon: Icon, id: givenId, className, type = 'text', ...rest },
  ref,
) {
  const autoId = useId();
  const id = givenId || autoId;
  return (
    <FieldFrame id={id} label={label} hideLabel={hideLabel} hint={hint} error={error} className={className}>
      <div className={cx('ui-input', Icon && 'ui-input--icon')}>
        {Icon && <Icon className="ui-input__icon" />}
        <input
          ref={ref}
          id={id}
          type={type}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(id, hint, error)}
          {...rest}
        />
      </div>
    </FieldFrame>
  );
});

/**
 * A search box: magnifier, a clear button when not empty, an optional key
 * hint ("/"). `onChange` receives the text; `label` is its accessible name
 * (visually hidden unless showLabel).
 */
export const SearchField = forwardRef(function SearchField(
  { label, showLabel = false, value, onChange, onClear, shortcut, className, quiet = false, id: givenId, onKeyDown, ...rest },
  ref,
) {
  const { t } = useTranslation();
  const autoId = useId();
  const id = givenId || autoId;
  const clear = () => {
    onChange('');
    if (onClear) onClear();
  };
  return (
    <div className={cx('ui-field', 'ui-search', quiet && 'ui-search--quiet', className)}>
      <label htmlFor={id} className={showLabel ? 'ui-field__label' : 'visually-hidden'}>{label}</label>
      <div className="ui-input ui-input--icon">
        <SearchIcon className="ui-input__icon" />
        <input
          ref={ref}
          id={id}
          type="search"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape' && value) {
              e.preventDefault();
              clear();
            }
            if (onKeyDown) onKeyDown(e);
          }}
          autoComplete="off"
          {...rest}
        />
        {value ? (
          <button type="button" className="ui-input__clear" onClick={clear} aria-label={t('ui.clearSearch')}>
            <CloseIcon size={16} />
          </button>
        ) : shortcut ? (
          <kbd className="ui-kbd" aria-hidden="true">{shortcut}</kbd>
        ) : null}
      </div>
    </div>
  );
});

export const Select = forwardRef(function Select(
  { label, hideLabel, hint, error, id: givenId, className, children, ...rest },
  ref,
) {
  const autoId = useId();
  const id = givenId || autoId;
  return (
    <FieldFrame id={id} label={label} hideLabel={hideLabel} hint={hint} error={error} className={className}>
      <div className="ui-select">
        <select
          ref={ref}
          id={id}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(id, hint, error)}
          {...rest}
        >
          {children}
        </select>
        <ChevronDownIcon className="ui-select__chevron" />
      </div>
    </FieldFrame>
  );
});

export const Textarea = forwardRef(function Textarea(
  { label, hideLabel, hint, error, id: givenId, className, rows = 4, ...rest },
  ref,
) {
  const autoId = useId();
  const id = givenId || autoId;
  return (
    <FieldFrame id={id} label={label} hideLabel={hideLabel} hint={hint} error={error} className={className}>
      <textarea
        ref={ref}
        id={id}
        rows={rows}
        className="ui-textarea"
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, hint, error)}
        {...rest}
      />
    </FieldFrame>
  );
});

export const Checkbox = forwardRef(function Checkbox({ label, hint, className, id: givenId, ...rest }, ref) {
  const autoId = useId();
  const id = givenId || autoId;
  return (
    <div className={cx('ui-check', className)}>
      <input ref={ref} id={id} type="checkbox" aria-describedby={hint ? `${id}-hint` : undefined} {...rest} />
      <label htmlFor={id}>
        <span>{label}</span>
        {hint && <span id={`${id}-hint`} className="ui-field__hint">{hint}</span>}
      </label>
    </div>
  );
});

/** An on/off switch (role="switch"); onChange receives the new boolean. */
export function Switch({ label, checked, onChange, disabled, className, id: givenId }) {
  const autoId = useId();
  const id = givenId || autoId;
  return (
    <div className={cx('ui-switch', className)}>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={!!checked}
        disabled={disabled}
        className="ui-switch__track"
        onClick={() => onChange(!checked)}
      >
        <span className="ui-switch__thumb" />
      </button>
      <label htmlFor={id}>{label}</label>
    </div>
  );
}
